import { type ReactElement } from 'react';
import { Link } from 'react-router';
import { useIsAuthenticated } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { TdsButton, TdsIcon } from '@scania/tegel-react';
import styles from './style.module.scss';
import { siteConfig } from '@/app/config/global';
import Navbar from '@/common/components/Navbar';
import SiteNoticeBanners from '@/common/components/SiteNoticeBanners';
import { getReleaseNotesQueryOptions } from '@/features/BatteryMonitoring/services/battery-monitoring-api/utils/get-release-notes-query-options';

const releaseDateFormatter = new Intl.DateTimeFormat('en', {
  month: 'short',
  timeZone: 'UTC',
  year: 'numeric',
});

const formatReleaseDate = (date: string): string => releaseDateFormatter.format(new Date(date));

const openAppButton = (
  <TdsButton text="Open app" variant="primary" modeVariant="primary" size="lg">
    <TdsIcon slot="icon" name="arrow_right" />
  </TdsButton>
);

const LandingPage = (): ReactElement => {
  const isAuthenticated = useIsAuthenticated();
  const releaseNotesQuery = useQuery({
    ...getReleaseNotesQueryOptions(),
    enabled: isAuthenticated,
  });

  return (
    <>
      <Navbar title={siteConfig.title} userFirstName={''} userLastName={''} isSignedIn={false} />
      <SiteNoticeBanners />
      <div className={styles.container}>
        <section className={styles.left}>
          <TdsIcon name="24v_battery" size="150px" />
          <h1 className={styles.title}>BATTERY MONITORING</h1>
          <p className={styles.subtitle}>
            <strong>Monitor</strong> and analyze important health parameters of battery systems in BEVs rolling fleets
          </p>
          {isAuthenticated ? (
            <Link to="/app">{openAppButton}</Link>
          ) : (
            <TdsButton text="Open app" variant="primary" modeVariant="primary" size="lg" disabled>
              <TdsIcon slot="icon" name="arrow_right" />
            </TdsButton>
          )}
        </section>
        <section className={styles.right}>
          <div className={styles['release-info']}>
            <h2 className={styles['release-header']}>Release information:</h2>
            <div className={styles['release-notes']}>
              {releaseNotesQuery.data?.releases.map((release) => (
                <div key={release.version} className={styles['release-version']}>
                  <p className={styles['version-header']}>
                    {release.version} Released ({formatReleaseDate(release.date)}):
                  </p>
                  <ul className={styles['version-list']}>
                    {release.changes.map((change) => (
                      <li key={change}>{change}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LandingPage;
